import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../../../axiosInstance";
import Swal from "sweetalert2"; // Import SweetAlert2
import withReactContent from "sweetalert2-react-content"; // Import React components for SweetAlert2
import Papa from "papaparse";

import Sidebar from "../../components/Sidebar";
import Navbar from "../../components/Navbar";
import LoadingTable from "../../components/LoadingTable";
import { Link } from "react-router-dom";
import { useNavigate,useParams } from "react-router-dom";
import toast from "react-hot-toast";

const MySwal = withReactContent(Swal); // Create a SweetAlert2 instance with React components

function AllUser() {
      const { id,status } = useParams();

  const navigate = useNavigate();
  const importInputRef = useRef(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({
    percent: 0,
    total: 0,
    created: 0,
    updated: 0,
    skipped: 0,
    message: "",
    status: "idle",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setlimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [phoneTerm, setPhoneTerm] = useState("");
  const [orderIdTerm, setOrderIdTerm] = useState("");
  const showVerificationColumn = !(id === '2' && (status === '1' || status === '0'));
  const showDeleteInStatusColumn = id === '1' || (id === '2' && status === '1');
  const pageTitle =
    id === '1'
      ? (status === '0' ? 'Inactive Staff' : status === '1' ? 'Active Staff' : 'Employee')
      : id === '2'
        ? (status === '1' ? 'Active Customer' : status === '0' ? 'Inactive Customer' : 'Customer')
        : id === '0'
          ? 'Admin'
          : '';

  const csvColumns = [
    { label: "ID", value: "_id" },
    { label: "User ID", value: "userId" },
    { label: "Full Name", value: "username" },
    { label: "Email", value: "email" },
    { label: "Phone", value: "phone" },
    { label: "Type", value: "type" },
    { label: "Employee Type", value: "empType" },
    { label: "Status", value: "status" },
    { label: "Verified", value: "verified" },
    { label: "Wallet", value: "wallet" },
    { label: "Address", value: "address" },
    { label: "City", value: "city" },
    { label: "State", value: "statename" },
    { label: "Pincode", value: "pincode" },
    { label: "Profile", value: "profile" },
    { label: "Online", value: "online" },
    { label: "Local", value: "Local" },
    { label: "Commission", value: "commission" },
    { label: "Local Commission", value: "LocalCommission" },
    { label: "Outstation Commission", value: "outCommission" },
    { label: "Zone", value: "zone" },
    { label: "Created At", value: "createdAt" },
  ];

  const makeExportFileName = () => {
    const safeTitle = pageTitle.toLowerCase().replace(/\s+/g, "-") || "users";
    const today = new Date().toISOString().slice(0, 10);
    return `${safeTitle}-${today}.csv`;
  };

  const handleExportUsers = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        page: 1,
        limit: 100000,
        search: searchTerm,
        type: id,
        status: status ? status : "",
        phone: phoneTerm,
        orderId: orderIdTerm,
      });
      const response = await axiosInstance.get(`/admin/all-user?${params.toString()}`, {
        validateStatus: (statusCode) => statusCode === 200 || statusCode === 404,
      });
      const users = response.data?.users || [];
      const csv = Papa.unparse({
        fields: csvColumns.map((column) => column.label),
        data: users.map((user) =>
          csvColumns.reduce((row, column) => {
            row[column.label] = user?.[column.value] ?? "";
            return row;
          }, {})
        ),
      });
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = makeExportFileName();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Users exported successfully!");
    } catch (error) {
      console.error("Error exporting users:", error);
      toast.error(error.response?.data?.message || "Failed to export users");
    } finally {
      setExporting(false);
    }
  };

  const handleImportUsers = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const batchSize = 500;

    setImporting(true);
    setImportProgress({
      percent: 0,
      total: 0,
      created: 0,
      updated: 0,
      skipped: 0,
      message: "Reading CSV file...",
      status: "reading",
    });
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (result) => {
        try {
          const rows = (result.data || []).filter((row) =>
            Object.values(row || {}).some((value) => String(value || "").trim())
          );

          if (!rows.length) {
            toast.error("CSV file has no user rows");
            setImportProgress((previous) => ({
              ...previous,
              message: "CSV file has no user rows",
              status: "error",
            }));
            return;
          }

          setImportProgress({
            percent: 0,
            total: rows.length,
            created: 0,
            updated: 0,
            skipped: 0,
            message: `Starting import for ${rows.length} users...`,
            status: "uploading",
          });

          let created = 0;
          let updated = 0;
          let skipped = 0;
          let processed = 0;
          const totalBatches = Math.ceil(rows.length / batchSize);

          for (let start = 0; start < rows.length; start += batchSize) {
            const batch = rows.slice(start, start + batchSize);
            const batchNumber = Math.floor(start / batchSize) + 1;

            setImportProgress((previous) => ({
              ...previous,
              message: `Uploading batch ${batchNumber} of ${totalBatches}...`,
              status: "uploading",
            }));

            const response = await axiosInstance.post("/admin/import-users", {
              users: batch,
              type: id,
              status: status ? status : "1",
            });

            created += response.data?.created || 0;
            updated += response.data?.updated || 0;
            skipped += response.data?.skipped?.length || 0;
            processed += batch.length;

            setImportProgress({
              percent: Math.round((processed / rows.length) * 100),
              total: rows.length,
              created,
              updated,
              skipped,
              message: `Processed ${processed} of ${rows.length} users`,
              status: processed >= rows.length ? "done" : "processing",
            });
          }

          setImportProgress({
            percent: 100,
            total: rows.length,
            created,
            updated,
            skipped,
            message: `Import complete: ${created} created, ${updated} updated, ${skipped} skipped`,
            status: "done",
          });
          toast.success(`Import complete: ${created} created, ${updated} updated, ${skipped} skipped`);
          fetchData();
        } catch (error) {
          console.error("Error importing users:", error);
          setImportProgress((previous) => ({
            ...previous,
            message: error.response?.data?.message || "Failed to import users",
            status: "error",
          }));
          toast.error(error.response?.data?.message || "Failed to import users");
        } finally {
          setImporting(false);
          event.target.value = "";
        }
      },
      error: (error) => {
        console.error("CSV parse error:", error);
        toast.error("Failed to read CSV file");
        setImportProgress((previous) => ({
          ...previous,
          message: "Failed to read CSV file",
          status: "error",
        }));
        setImporting(false);
        event.target.value = "";
      },
    });
  };

  const fetchData = async (options = {}) => {
    try {
      setLoading(true);
      const pageToFetch = options.page || currentPage;
      const params = new URLSearchParams({
        page: pageToFetch,
        limit,
        search: searchTerm.trim(),
        type: id,
        status: status ? status : '',
        phone: phoneTerm.trim(),
        orderId: orderIdTerm.trim(),
      });
      const response = await axiosInstance.get(
        `/admin/all-user?${params.toString()}`
      );
      setData(response.data.users || []);
      setCurrentPage(response.data.currentPage || pageToFetch);
      setTotalPages(response.data.totalPages || 1);
      console.log(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 404) {
        setData([]);
        setCurrentPage(options.page || 1);
        setTotalPages(1);
      } else {
        toast.error(error.response?.data?.message || "Error fetching users");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData({ page: currentPage });
  }, [currentPage, limit,id,status]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Function to handle delete
  const handleDelete = (userId) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "This user will be deleted or disabled based on their order and wallet history.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete user!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
      customClass: {
        popup: "custom-swal-popup", // Add a class to the entire dialog
        confirmButton: "btn-danger", // Add a class to the confirm button
      },
    }).then((result) => {
      if (result.isConfirmed) {
        axiosInstance
          .put(`/admin/soft-delete-user/${userId}`)
          .then((response) => {
            // Refresh the data or update the state after successful deletion
            fetchData();
            toast.success(response.data?.message || "User deleted successfully!");
          })
          .catch((error) => {
            console.error("Error deleting data:", error);
            toast.error(error.response?.data?.message || "Error deleting user");
          });
      }
    });
  };

  const handleStatusChange = (StatusId) => async (event) => {
    const newStatus = event.target.value;
    setLoading(true);

    try {
      await axiosInstance.put(`/admin/update-user/${StatusId}`, {
        status: newStatus,
      });
      setLoading(false);
      fetchData(currentPage);
      toast.success("Status Updated success!");
    } catch (error) {
      console.error("Error updating rating status:", error);
      setLoading(false);
    }
  };

  const handleVerifyChange = (StatusId) => async (event) => {
    const newStatus = event.target.value;
    setLoading(true);

    try {
      await axiosInstance.put(`/admin/update-user-verify/${StatusId}`, {
        status: newStatus,
      });
      setLoading(false);
      fetchData(currentPage);
      toast.success("Verification Updated success!");
    } catch (error) {
      console.error("Error updating rating status:", error);
      setLoading(false);
    }
  };

  const handleSearchInputChange = (e) => {
    setCurrentPage(1);
    setTotalPages(1);
    setSearchTerm(e.target.value);
  };

  const handlePhoneInputChange = (e) => {
    setCurrentPage(1);
    setTotalPages(1);
    setPhoneTerm(e.target.value);
  };

  const handleOrderIdInputChange = (e) => {
    setCurrentPage(1);
    setTotalPages(1);
    setOrderIdTerm(e.target.value);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchData({ page: 1 });
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  
    const [WalletLoading, setWalletLoading] = useState(true);


    let initialWalletData = {
        _id: null,
        TotalAmount: 0,
        WalletType: '',
        Amount: null,
        Comment: '',
    };

    const [WalletInputs, setWalletInputs] = useState(initialWalletData);

   const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'Amount' && parseFloat(value) < 0) {
            // You can handle the error, show a message, or prevent further action here
            // For now, let's reset the value to 0
            // You might want to handle this differently based on your UI/UX requirements
            toast.error('Negative numbers not allowed for Amount');
            return;
        } else {
            setWalletInputs((prevData) => ({ ...prevData, [name]: type === 'checkbox' ? checked : value }));

        }

    };



    const selectWallet = async (walletId) => {

        const credentials = {
            id: walletId,
        };
        setWalletLoading(true);
        try {
            const { data } = await axiosInstance.post("/auth-user", credentials);
            const { success, existingUser } = data;

            if (success) {
                setWalletInputs((prevData) => ({
                    ...prevData,
                    TotalAmount: existingUser.wallet || 0,
                    _id: existingUser._id,
                }));

                console.log("Message from backend:", existingUser);
            }
        } catch (error) {
            console.error("Error during login:", error);
            // Handle network errors, API issues, etc.
            toast.error(error.response.data.message);
        } finally {
            setWalletLoading(false);
        }

    }


    const WalletUpdate = async (walletId) => {

        setWalletLoading(true);

        if (WalletInputs.WalletType === '') {
            toast.error('Please Choose Add money OR Withdraw Money')
            return null;
        }
        if (WalletInputs.Amount === null) {
            toast.error('Please Type Amount More Then 0')
            return null;
        }
        if (WalletInputs.Comment === '') {
            toast.error('Please Type Comment')
            return null;
        }

        try {

            // await axiosInstance.post("/admin/update-wallet", WalletInputs);

            await toast.promise(axiosInstance.post("/admin/update-wallet", WalletInputs), {
                loading: "Updating funds on wallet...", // Loading message
                success: "Wallet Updated successfully!", // Success message
                error: "Failed to added funds.", // Error message
            });


        } catch (error) {
            console.error("Error On Wallet", error);
            toast.error(error.response.data.message);
        } finally {
            setWalletLoading(false);
            fetchData();
            setWalletInputs((prevData) => ({
                ...prevData,
                _id: null,
                TotalAmount: 0,
                WalletType: '',
                Amount: null,
                Comment: '',
            }));

        }

    };
    
  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="py-4">
          <div className="d-flex justify-content-between w-100 flex-wrap">
            <div className="mb-3 mb-lg-0">
              <h1 className="h4"> All {pageTitle} </h1>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="table-responsive py-4">
            {loading ? (
              <LoadingTable />
            ) : (
              <>
                <div className="datatable-top">
                  <div className="datatable-dropdown">
                    <label>
                      <select
                        className="datatable-selector"
                        value={limit}
                        onChange={(e) => {
                          setlimit(parseInt(e.target.value, 10));
                          setCurrentPage(1); // Reset current page to 1 when the limit changes
                        }}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={20}>20</option>
                        <option value={25}>25</option>
                      </select>{" "}
                      entries per page
                    </label>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div className="datatable-search">
                      <input
                        className="datatable-input"
                        type="text"
                        placeholder="Search name, email, phone..."
                        value={searchTerm}
                        onChange={handleSearchInputChange}
                        onKeyDown={handleSearchKeyDown}
                      />
                    </div>
                    {id === '1' && (
                      <>
                        <div className="datatable-search">
                          <input
                            className="datatable-input"
                            type="text"
                            placeholder="Search by phone..."
                            value={phoneTerm}
                            onChange={handlePhoneInputChange}
                            onKeyDown={handleSearchKeyDown}
                          />
                        </div>
                        <div className="datatable-search">
                          <input
                            className="datatable-input"
                            type="text"
                            placeholder="Search by order ID..."
                            value={orderIdTerm}
                            onChange={handleOrderIdInputChange}
                            onKeyDown={handleSearchKeyDown}
                          />
                        </div>
                      </>
                    )}
                    <button className="btn btn-primary" onClick={handleSearch}>
                      {" "}
                      Search
                    </button>
                    <input
                      ref={importInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      className="d-none"
                      onChange={handleImportUsers}
                    />
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={handleExportUsers}
                      disabled={exporting || importing}
                    >
                      {exporting ? "Exporting..." : "Export"}
                    </button>
                    <button
                      className="btn btn-success"
                      type="button"
                      onClick={() => importInputRef.current?.click()}
                      disabled={exporting || importing}
                    >
                      {importing ? "Importing..." : "Import"}
                    </button>
                  </div>
                </div>

                {importProgress.status !== "idle" && (
                  <div className="px-4 pb-3">
                    <div className={`alert mb-0 ${importProgress.status === "error" ? "alert-danger" : importProgress.status === "done" ? "alert-success" : "alert-info"}`}>
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                        <strong>{importProgress.message}</strong>
                        <span>{importProgress.percent}%</span>
                      </div>
                      <div className="progress mb-3" style={{ height: 8 }}>
                        <div
                          className={`progress-bar ${importProgress.status === "error" ? "bg-danger" : "bg-success"}`}
                          role="progressbar"
                          style={{ width: `${importProgress.percent}%` }}
                          aria-valuenow={importProgress.percent}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        />
                      </div>
                      <div className="d-flex flex-wrap gap-3 small">
                        <span>Total: <strong>{importProgress.total}</strong></span>
                        <span>Added: <strong>{importProgress.created}</strong></span>
                        <span>Updated: <strong>{importProgress.updated}</strong></span>
                        <span>Skipped: <strong>{importProgress.skipped}</strong></span>
                      </div>
                    </div>
                  </div>
                )}


    <div
                                    className="modal fade"
                                    id="modal-wallet"
                                    tabIndex={-1}
                                    aria-labelledby="modal-wallet"
                                    style={{ display: "none" }}
                                    aria-hidden="true"
                                >
                                    <div className="modal-dialog modal-dialog-centered" role="document">
                                        <div className="modal-content">
                                            <div className="modal-header">
                                                <h2 className="h6 modal-title">  <i className="ri-wallet-fill"></i> Wallet </h2>
                                                <button
                                                    type="button"
                                                    className="btn-close"
                                                    data-bs-dismiss="modal"
                                                    aria-label="Close"
                                                />
                                            </div>


                                            <div className="modal-body">
                                                {WalletLoading ? (
                                                    <>

                                                        <p className="skeleton mb-4" style={{ height: 50 }} > </p>
                                                        <div className='d-flex gap-2'>
                                                            <p className="skeleton mb-4 w-100" style={{ height: 50 }} > </p>
                                                            <p className="skeleton mb-4 w-100" style={{ height: 50 }} > </p>
                                                        </div>
                                                        <p className="skeleton mb-4" style={{ height: 50 }} > </p>
                                                        <p className="skeleton mb-4" style={{ height: 50 }} > </p>
                                                    </>
                                                ) : (
                                                    <>

                                                        <div className="mb-0">
                                                            <div className="form-group mt-3">
                                                                <label htmlFor="TotalAmount">Total Money</label>{" "}
                                                                <input
                                                                    className="form-control"
                                                                    id="TotalAmount"
                                                                    type="text"
                                                                    name="TotalAmount"
                                                                    value={Math.round(WalletInputs.TotalAmount)}
                                                                    disabled
                                                                />
                                                            </div>

                                                            <div className="form-group mt-3 ">
                                                                <label htmlFor="wallet">Please Choose <span className='text-danger'> (Required)</span></label>

                                                            </div>


                                                            <label
                                                                className={`btn  me-3 ${WalletInputs.WalletType === 'plus' ? WalletInputs.WalletType !== '' && 'btn-primary' : 'btn-outline-primary'}`}
                                                                type="button" htmlFor="addmoney" >
                                                                < input
                                                                    className="form-check-input d-none"
                                                                    type="radio"
                                                                    name="WalletType"
                                                                    id="addmoney" value={'plus'}
                                                                    onChange={handleChange}
                                                                />
                                                                Add Money
                                                            </label>

                                                            <label
                                                                className={`btn me-3 ${WalletInputs.WalletType === 'minus' ? WalletInputs.WalletType !== '' && 'btn-primary' : 'btn-outline-primary'}`}
                                                                type="button" htmlFor="Withdrawmoney">
                                                                <input
                                                                    className="form-check-input d-none"
                                                                    type="radio"
                                                                    name="WalletType"
                                                                    onChange={handleChange}
                                                                    id="Withdrawmoney" value={'minus'}
                                                                />
                                                                Withdraw money</label>


                                                            {/* <select
                            className="form-control select"
                            name="status"
                            style={{ width: "100%" }} onChange={handleDriverChange(OrderId)}
                          >
                            <option > Select Vendor</option>


                            {AllDriver.length > 0 ? (

                              AllDriver.map(vendor => (
                                <option value={vendor._id} key={vendor._id} selected={vendor._id === DriverId}
                                >{vendor.username}</option>
                              ))
                            ) : (
                              <p>No Vendor found.</p>
                            )}



                          </select> */}

                                                        </div>

                                                        <div className="form-group mt-3">
                                                            <label htmlFor="Amount">Add / Withdraw Money <span className='text-danger'> (Required)</span></label>
                                                            <input
                                                                className="form-control"
                                                                id="Amount"
                                                                type="number"
                                                                name="Amount"
                                                                onChange={handleChange}
                                                                value={WalletInputs.Amount}
                                                            />
                                                        </div>

                                                        <div className="form-group mt-3">
                                                            <label htmlFor="Comment">Comment <span className='text-danger'> (Required)</span></label>
                                                            <textarea
                                                                className="form-control"
                                                                id="Comment"
                                                                type="text"
                                                                name="Comment"
                                                                onChange={handleChange}
                                                                value={WalletInputs.Comment}

                                                            />
                                                        </div>

                                                    </>
                                                )}

                                            </div>
                                            <div className="modal-footer">
                                                <button
                                                    className="btn btn-secondary"
                                                    type="button"
                                                    data-bs-dismiss="modal" onClick={() => WalletUpdate(WalletInputs._id)}
                                                >
                                                    Update Wallet
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-gray-300 ms-auto"
                                                    data-bs-dismiss="modal"
                                                >
                                                    Close
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>


                <table className="table table-flush">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Full Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Rating</th>
                      <th>Status</th>
                      {showVerificationColumn && <th>Verification</th>}
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row) => (
                      <tr key={row._id}>
                        <td>
                          {row._id}
                        </td>
                        <td>
                          {row.username} <br />
                          <span className="bg-success small text-white px-3 py-1 rounded-pill">  
                             {row.type === 1
                               ? (row.empType === 2 ? 'Driver' : 'Employee')
                               : row.type === 3
                                 ? 'Vendor'
                                 : row.type === 2
                                   ? 'Customer'
                                   : row.type === 0
                                     ? 'Admin'
                                     : ''} </span>
                          <br/>
                          <button type="button" data-bs-target="#modal-wallet" data-bs-toggle="modal" onClick={() => selectWallet(row._id)}  className="bg-primary small text-white p-1 rounded-pill mt-2 d-inline-block px-2">  
                              <i class="ri-wallet-line"></i> {Math.round(Math.abs(row.wallet))}

                              </button>

                            </td>
                        <td> {row.email} </td>
                        <td> {row.phone} </td>
                        <td>
                          <strong>{Number(row.averageRating || 0).toFixed(1)}</strong>
                          <br />
                          <span className="small text-muted">
                            {row.totalReviews || 0} reviews
                          </span>
                        </td>
                        <td>
                          {row.status !== '0' ? (
                            <button
                              type="button"
                              className="btn btn-danger"
                              onClick={() => handleDelete(row._id)}
                            >
                              Delete <i className="ri-delete-bin-line"></i>
                            </button>
                          ) : (
                            <>
                               <div className="alert alert-danger small px-2 py-1 text-center" role="alert">
Sorry, already deleted!
</div>
                            </>
                          )}
                        </td>
                        {showVerificationColumn && (
                          <td>
                            <select
                              style={{ width: "fit-content" }}
                              className={`form-control select ${row.verified === 0
                                ? "text-white btn-danger" // For row.verified === 0, apply text-white and btn-danger
                                : row.verified === 2
                                  ? "text-white btn-warning" // For row.verified === 2, apply text-white and btn-warning
                                  : "text-white btn-success" // For row.verified === 1 or any other value, apply text-white and btn-success
                                }`}
                              value={row.verified}
                              name="status"
                              onChange={handleVerifyChange(row._id)}
                            >
                              <option value="1">Verified</option>
                              <option value="2">Pending</option>
                              <option value="0">unverified </option>
                            </select>

                            <br />
                          </td>
                        )}
                        <td>
                          <Link
                            className="btn btn-secondary me-2"
                            to={`/user/${row._id}`}
                          >
                            View <i className="ri-user-fill"></i>
                          </Link>
                          {/* {!showDeleteInStatusColumn && (
                            <button
                              type="button"
                              className="btn btn-danger"
                              onClick={() => handleDelete(row._id)}
                            >
                              Delete <i className="ri-delete-bin-line"></i>
                            </button>
                          )} */}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="datatable-bottom">
                  <div className="datatable-info"> </div>
                  <nav className="datatable-pagination">
                    <ul class="datatable-pagination-list">
                      <li
                        className={`datatable-pagination-list-item ${currentPage === 1
                          ? "datatable-hidden"
                          : "datatable-active"
                          }`}
                      >
                        <button
                          data-page="1"
                          class="datatable-pagination-list-item-link"
                          onClick={handlePreviousPage}
                          disabled={loading || currentPage === 1}
                          aria-label="Page 1"
                        >
                          ‹
                        </button>
                      </li>
                      <li>
                        <div className="datatable-info py-1 px-3">
                          Showing Page{" "}
                          <strong>
                            Showing Page {currentPage} of {totalPages}
                          </strong>{" "}
                        </div>
                      </li>
                      <li
                        className={`datatable-pagination-list-item ${currentPage === totalPages
                          ? "datatable-hidden"
                          : "datatable-active"
                          }`}
                      >
                        <button
                          data-page="2"
                          class="datatable-pagination-list-item-link"
                          aria-label="Page 2"
                          onClick={handleNextPage}
                          disabled={loading || currentPage === totalPages}
                        >
                          {" "}
                          {loading ? "Loading..." : "›"}{" "}
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </>
  );
}

export default AllUser;
